import { DOCUMENT } from '@angular/common';
import { inject, Injector, type Provider, type Signal } from '@angular/core';
import type { MatDialogRef } from '@angular/material/dialog';
import { DialogService, type DialogResult } from '@quipex/shared/ui';
import { take } from 'rxjs';
import { ReadStatus } from './read-status';

export interface WhatsNewDialogLauncher {
    readonly hasUnread: Signal<boolean>;
    open(): Promise<void>;
}

export function provideWhatsNew(): Provider[] {
    return [ReadStatus];
}

export function injectWhatsNewDialog(): WhatsNewDialogLauncher {
    const document = inject(DOCUMENT);
    const dialogService = inject(DialogService);
    const readStatus = inject(ReadStatus);
    const injector = inject(Injector);
    let openDialogRef: MatDialogRef<unknown, DialogResult> | undefined;
    let openingDialog: Promise<void> | undefined;

    return {
        hasUnread: readStatus.hasUnread,
        open: async () => {
            if (openDialogRef) return;

            const focusOrigin = getFocusOrigin(document);
            openingDialog ??= import('./article-dialog')
                .then(({ ArticleDialog }) => {
                    const dialogRef = dialogService.openDialog(
                        { component: ArticleDialog },
                        {
                            autoFocus: 'first-heading',
                            disableClose: false,
                            restoreFocus: false,
                            size: 'md',
                            injector,
                        }
                    );

                    openDialogRef = dialogRef;
                    dialogRef
                        .afterClosed()
                        .pipe(take(1))
                        .subscribe(() => {
                            openDialogRef = undefined;
                            focusOrigin?.focus();
                        });
                })
                .finally(() => {
                    openingDialog = undefined;
                });

            await openingDialog;
        },
    };
}

function getFocusOrigin(document: Document): HTMLElement | null {
    const activeElement = document.activeElement;
    const HTMLElementConstructor = document.defaultView?.HTMLElement;

    return HTMLElementConstructor &&
        activeElement instanceof HTMLElementConstructor
        ? activeElement
        : null;
}
